import { Box, Flex, Select } from '@chakra-ui/react';
import { modelAction } from 'actions/model';
import { setStorage, STORAGE_KEY } from 'helpers/storage';
import { ChevronDown, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { UserService } from 'services/user/user.service';
import { authInstance } from 'utils/auth';

const languages = [
  { code: 'en', lang: 'English' },
  { code: 'ja', lang: '日本語' },
  { code: 'ar', lang: 'العربية' },
  { code: 'de', lang: 'Deutsch' },
  { code: 'es', lang: 'Español' },
  { code: 'fr', lang: 'Français' },
  { code: 'nl', lang: 'Nederlands' },
  { code: 'pt', lang: 'Português (Brasil)' },
  { code: 'zh', lang: '简体中文' },
];

/**
 * Language switcher styled as a bordered pill (mockup): globe icon on the LEFT,
 * the language label, and a trailing chevron. The native <select> sits on top,
 * transparent, so it keeps the exact i18n switching wiring + native a11y/options.
 */
const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();

  const handleChangeLocale = async (lng: string) => {
    i18n.changeLanguage(lng);
    setStorage(STORAGE_KEY.I18_NEXT_LANGUAGE, lng);
    await UserService.changeLocale(lng);
    // Wait a bit for server to update locale, then refresh token
    await new Promise((resolve) => setTimeout(resolve, 500));
    await authInstance.forceRefreshToken();
    dispatch(modelAction.fetchModelsRequest());
  };

  const current = languages.find((l) => l.code === i18n.language) ?? languages[0];

  return (
    <Flex
      pos='relative'
      align='center'
      gap={2}
      h='36px'
      pr={2.5}
      pl={3}
      color='text.default'
      fontSize='sm'
      fontWeight={500}
      bg='bg.surface'
      borderWidth='1px'
      borderColor='border.default'
      borderRadius='full'
      _hover={{ borderColor: 'border.strong' }}
      _focusWithin={{ borderColor: 'primary.solid' }}
      transition='border-color 0.14s ease-out'
    >
      <Box as={Globe} flexShrink={0} boxSize='16px' color='text.muted' />
      {/* Icon-only on mobile (QA: remove the "English" label on the menu like the old version);
          keep the label on desktop where there's room. */}
      <Box as='span' display={{ base: 'none', md: 'inline' }} whiteSpace='nowrap'>
        {current.lang}
      </Box>
      <Box as={ChevronDown} flexShrink={0} boxSize='16px' color='text.subtle' />
      <Select
        pos='absolute'
        w='100%'
        h='100%'
        opacity={0}
        cursor='pointer'
        aria-label='Select language'
        icon={<></>}
        inset={0}
        onChange={(e) => handleChangeLocale(e.target.value)}
        value={i18n.language}
      >
        {languages.map((item) => (
          <option key={item.code} value={item.code}>
            {item.lang}
          </option>
        ))}
      </Select>
    </Flex>
  );
};

export default LanguageSelector;
